import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { showError } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import { useFundraisingEvents } from '../../hooks';

export interface IFundraisingSummaryMetrics {
  donationTotal: number;
  growthCredits: number;
  isLoading: boolean;
  hasError: boolean;
}

// Coalesce a burst of donation events (e.g. several arriving within seconds
// of one another) into a single refetch.
const REFETCH_DEBOUNCE_MS = 500;

/**
 * Aggregates summary metrics for fundraising activity.
 *
 * On mount, fetches authoritative totals from `GET /donations/stats`
 * (computed live from breadcrumbs on the backend). When new donation SSE
 * events arrive, debounces a refetch from the same endpoint so the totals
 * stay in sync with the transaction table without drifting (refunds,
 * voids, duplicate deliveries are reconciled automatically).
 *
 * @returns Aggregated donation total, growth credits, loading and error state.
 */
function useFundraisingSummaryMetrics(): IFundraisingSummaryMetrics {
  const { t } = useTranslation();
  const { Breadcrumbs } = useStore();
  const [donationTotal, setDonationTotal] = useState(0);
  const [growthCredits, setGrowthCredits] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Tracks whether the component is still mounted so async fetches can bail
  // before calling setState on an unmounted component.
  const isAliveRef = useRef(true);
  const refetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchStats = useCallback(async (): Promise<void> => {
    try {
      const stats = await Breadcrumbs.getDonationStats();
      if (!isAliveRef.current) {
        return;
      }
      setDonationTotal(stats.total_donation_volume);
      setGrowthCredits(stats.total_growth_credits);
      setHasError(false);
    } catch (error: unknown) {
      if (!isAliveRef.current) {
        return;
      }
      setHasError(true);
      showError(error, t);
    }
  }, [Breadcrumbs, t]);

  useEffect(() => {
    isAliveRef.current = true;

    fetchStats().finally(() => {
      if (isAliveRef.current) {
        setIsLoading(false);
      }
    });

    return () => {
      isAliveRef.current = false;
      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current);
        refetchTimeoutRef.current = null;
      }
    };
  }, [fetchStats]);

  useFundraisingEvents({
    onDonationCreated: () => {
      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current);
      }
      refetchTimeoutRef.current = setTimeout(() => {
        refetchTimeoutRef.current = null;
        fetchStats();
      }, REFETCH_DEBOUNCE_MS);
    },
  });

  return {
    donationTotal,
    growthCredits,
    isLoading,
    hasError,
  };
}

export default useFundraisingSummaryMetrics;
